<script lang="ts">
	import type { Vehicle } from './basic'
	import type { Mediator } from 'client/types/common'
	import type {
		VehicleBasicStageOne$result,
		InventoryTypeDetail$result,
		vehicleInventorySettingValues$result,
		CreateVehicle$input,
		InventoryOptionValueUpdate,
	} from '$houdini'
	import type { InventoryType, Model, Category, UserAccount, UserStatus } from 'utility/search-fields'
	import type { SvelteAsr } from 'types/common'
	import type { FlatQuestion, OptionValueMap } from 'utility/load-part'
	import type { VehicleMediatorProviders } from './basic'
	type SettingValues = vehicleInventorySettingValues$result['settingValues']

	//#region Svelte Components
	import Icon from '@isoftdata/svelte-icon'
	import Input from '@isoftdata/svelte-input'
	import Select from '@isoftdata/svelte-select'
	import Button from '@isoftdata/svelte-button'
	import QaList from '../../part/QaList.svelte'
	import Checkbox from '@isoftdata/svelte-checkbox'
	import TextArea from '@isoftdata/svelte-textarea'
	import YearInput from '@isoftdata/svelte-year-input'
	import DateInput from '@isoftdata/svelte-date-input'
	import Autocomplete from '@isoftdata/svelte-autocomplete'
	import VinLookupModal from 'components/VinLookupModal.svelte'
	import Card from '@isoftdata/svelte-card'
	import DuplicateVinModal from 'components/DuplicateVinModal.svelte'
	import PartMarketingFields from 'components/PartMarketingFields.svelte'
	import SiteAutocomplete from '@isoftdata/svelte-site-autocomplete'
	import HtpInfoButton from 'components/HtpInfoButton.svelte'
	import ImagePreview from 'components/ImagePreview.svelte'
	//#endregion

	//#region Utility Imports
	import { klona } from 'klona'
	import { dequal } from 'dequal'
	import { graphql } from '$houdini'
	import { parseISO, formatISO } from 'date-fns'
	import { hasPermission } from 'utility/permission'
	import { getEventValue } from '@isoftdata/browser-event'
	import { stringToBoolean } from '@isoftdata/utility-string'
	import { booleanToString } from '@isoftdata/utility-boolean'
	import { onMount, tick, getContext, onDestroy } from 'svelte'
	import { decodeVin as fetchDecodedVin } from 'utility/vin-decode'
	import { getModifiedIndicator } from 'utility/get-modified-indicator'
	import { getOptionValueMap, computeTypeFields } from 'utility/load-part'
	import { loadPartModelsForInventoryTypeAndManufacturer } from 'utility/search-fields'

	import session from 'stores/session'
	import vehicleStatuses from 'common/vehicle-statuses'
	import userLocalWritable from 'stores/user-local-writable'
	import type { i18n } from 'types/common'

	const { t: translate } = getContext<i18n>('i18next')
	//#endregion

	const mediator = getContext<Mediator<VehicleMediatorProviders>>('mediator')

	interface Props {
		//@region Exported Component Props
		asr: SvelteAsr
		vehicle: Vehicle
		originalVehicle: Vehicle
		locations?: string[]
		vehicleModels?: Model[]
		categories?: Category[]
		options?: FlatQuestion[]
		colors: {
			vehicleColorCodes: VehicleBasicStageOne$result['vehicleColorCodes']
			vehicleColors: VehicleBasicStageOne$result['vehicleColors']
		}
		unitTypes?: InventoryType[]
		userStatuses?: UserStatus[]
		userAccounts?: UserAccount[]
		inventoryConditions?: string[]
		states?: VehicleBasicStageOne$result['states']
		saleClasses?: VehicleBasicStageOne$result['saleClasses']
		glCategories?: VehicleBasicStageOne$result['glCategories']
		settingValues: vehicleInventorySettingValues$result['settingValues']
	}

	let {
		asr,
		vehicle = $bindable(),
		originalVehicle = $bindable(),
		locations = [],
		vehicleModels = $bindable([]),
		categories = $bindable([]),
		options = [],
		colors,
		unitTypes = [],
		userStatuses = [],
		userAccounts = [],
		inventoryConditions = [],
		states = [],
		saleClasses = [],
		glCategories = [],
		settingValues,
	}: Props = $props()
	//#endregion

	//#region Local Variables
	let vehicleManufacturers: InventoryTypeDetail$result['inventoryType']['partManufacturers'] = $state([])
	let optionValueMap = $state(getOptionValueMap({ inventoryOptions: [], serialized: false, serials: [] }))
	let vinLoading: boolean = $state(false)
	let isLoadingInventoryTypeDetailManufacturersCategories: boolean = $state(false)
	let isLoadingModels: boolean = $state(false)
	let requiredFieldsComplete: boolean = $derived(getRequiredFieldsComplete(vehicle, originalVehicle))
	let vinValidationError: string = $state('')
	let showDuplicateVinModal: boolean = $state(false)
	let inventoryTypeWithDetail: InventoryTypeDetail$result['inventoryType'] | null = $state(null)
	let qaList: QaList | undefined = $state(undefined)
	let sellerContact: string = $state(vehicle.sellerContact ?? '')
	let sellerContactLastName: string = $state(vehicle.sellerContact?.split(',')[0] ?? '')
	let sellerContactFirstName: string = $state(vehicle.sellerContact?.split(',')[1] ?? '')
	let vinLookupModal: VinLookupModal = $state()!
	let duplicateVinModal: DuplicateVinModal = $state()!
	let qaChanged: boolean = $state(false)
	let field = $derived(settingValues.inventory.fields)
	let vehicleChanged = $derived(qaChanged || !dequal(vehicle, originalVehicle))
	//#endregion

	//#region Permissions
	let canEditBasic = $derived(hasPermission('VEHICLES_CAN_EDIT_BASIC', vehicle.store.id))
	let canEditAdditionalInfo = $derived(hasPermission('VEHICLES_CAN_EDIT_ADDITIONAL_INFORMATION', vehicle.store.id))
	let canEditStockNumber = $derived(hasPermission('VEHICLES_CAN_EDIT_STOCK_NUMBER', vehicle.store.id))
	let canEditStatus = $derived(hasPermission('VEHICLES_CAN_EDIT_STATUS', vehicle.store.id))
	let canEditGlCategory = $derived(hasPermission('VEHICLES_CAN_EDIT_GL_CATEGORY', vehicle.store.id))
	let canEditWholeUnits = $derived(hasPermission('VEHICLES_CAN_EDIT_WHOLE_UNITS', vehicle.store.id)) // In web, this is currently used to unlock pricing fields
	let canEditQanda = hasPermission('VEHICLES_CAN_EDIT_QANDA') // In web, this is used to unlock the Q&A fields AND type data fields (flex fields)
	// let canEditWholeUnitAdvanced = hasPermission('VEHICLES_CAN_EDIT_WHOLE_UNIT_ADVANCED')
	// let canEditBids = hasPermission('VEHICLES_CAN_EDIT_BIDS')
	// let canEditNonBidUnits = hasPermission('VEHICLES_CAN_EDIT_NON_BID_UNITS')
	// let canViewVehiclePerformance = hasPermission('VEHICLES_CAN_VIEW_VEHICLE_PERFORMANCE')
	// let canChangeStockQuantity = hasPermission('VEHICLES_CAN_CHANGE_STOCK_QUANTITY')
	//#endregion

	$effect(() => mediator.publish('vehicleChanged', vehicleChanged))
	$effect(() => mediator.publish('requiredVehicleFieldsComplete', requiredFieldsComplete))

	const getModifiedHint = getModifiedIndicator(vehicle, originalVehicle, modifiedHint)

	$inspect(vehicle.wholeUnit)

	const unitTypeDetailQuery = graphql(`
		query InventoryTypeDetail($inventoryTypeId: Int!) {
			inventoryType(id: $inventoryTypeId) {
				typeData1History
				typeData2History
				typeData3History
				typeData4History
				typeLabel1
				typeLabel2
				typeLabel3
				typeLabel4
				vehicle
				requireSerialization
				typeSetId: setId
				name
				inventoryTypeId: id
				categories {
					id
					description
					name
				}
				partManufacturers: manufacturers {
					id
					name
					code
					active
				}
			}
		}
	`)

	const duplicateVinCheckQuery = graphql(`
		query DuplicateVinQuery($filter: VehicleFilter) {
			vehicles(filter: $filter) {
				items {
					id
					store {
						name
					}
					stockNumber
					enteredDate
					status
					userStatus
					inventoryType {
						id
						name
					}
					location
				}
			}
		}
	`)

	//The only fields we need to get back are what we need to pass to asr.go
	const createVehicleMutation = graphql(`
		mutation CreateVehicle($input: NewVehicle!) {
			createVehicle(input: $input) {
				id
				storeId
				lastChangedDate
			}
		}
	`)

	const updateVehicleMutation = graphql(`
		mutation UpdateVehicle($input: VehicleUpdate!) {
			updateVehicle(input: $input) {
				id
				storeId
				lastChangedDate
			}
		}
	`)

	const cardsOpen = userLocalWritable($session.id, 'vehicleScreenCardsOpen', {
		basic: true,
		salesMarketing: true,
		purchaseSeller: true,
		title: true,
		insurance: true,
	})

	async function checkForDuplicateVin(vin: string) {
		if (vin) {
			vin = vin.trim()
			let vehiclesWithSameVin =
				(await duplicateVinCheckQuery.fetch({ variables: { filter: { vin } } }))?.data?.vehicles?.items ?? []
			if (vehiclesWithSameVin.length > 0 && vehicle.id) {
				//Be sure to ignore the vehicle the user is currently editing
				vehiclesWithSameVin = vehiclesWithSameVin.filter(vehicle => vehicle.id !== vehicle.id)
			}
			if (vehiclesWithSameVin.length > 0) {
				//Let the user decide what to do since there is at least one existing vehicle with this same VIN
				await duplicateVinModal.open(vehiclesWithSameVin, vin)
			}
			return vehiclesWithSameVin
		} else {
			return []
		}
	}

	async function decodeVin(vin: string) {
		vin = vin.trim()
		if (vin) {
			try {
				vinLoading = true
				const decodedVin = await fetchDecodedVin(vin)
				if (decodedVin?.validation?.isValid) {
					vehicle.year = decodedVin.year ?? vehicle.year

					const decodedVinManufacturer = decodedVin.make?.toUpperCase()
					const vehicleManufacturerMatch = decodedVinManufacturer
						? vehicleManufacturers.find(make => make.name.toUpperCase() === decodedVinManufacturer)
						: undefined
					if (vehicleManufacturerMatch) {
						vehicle.manufacturer = vehicleManufacturerMatch
					}

					await loadAndSetModels(vehicle.wholeUnit.inventoryTypeId, vehicle.manufacturer?.id)
					if (vehicleManufacturerMatch && decodedVin.model) {
						const decodedVinModel = decodedVin.model.toUpperCase()
						const vehicleModelMatch = vehicleModels.find(model => model.name.toUpperCase() === decodedVinModel)
						vehicle.model = vehicleModelMatch ?? vehicle.model
					}

					if (vehicle.notes && vehicle.notes.trim().length > 0 && decodedVin?.notes) {
						vehicle.notes += '\n\n' + decodedVin.notes
					} else if (decodedVin.notes) {
						vehicle.notes = decodedVin.notes
					}
				}
				vinValidationError = decodedVin?.validation?.isValid ? '' : (decodedVin?.validation?.message ?? '')
			} catch (err) {
				console.error(err)
				vinValidationError = translate('vehicle.errorDecodingVin', 'Error decoding VIN')
			} finally {
				vinLoading = false
			}
		}
	}

	function transformInventoryOptionValueForMutation(optionValue: string | number | boolean | null) {
		if (typeof optionValue === 'number') {
			return optionValue.toString()
		} else if (typeof optionValue === 'boolean') {
			return booleanToString(optionValue)
		} else if (optionValue === null) {
			return ''
		} else {
			return optionValue
		}
	}

	const isValidYear = (year: string | number | null) => typeof year === 'number' && year.toString().length === 4

	async function saveVehicle(vehicle: Vehicle, optionValueMap: OptionValueMap) {
		const input: Omit<CreateVehicle$input['input'], 'inventoryTypeId' | 'storeId' | 'id'> = {
			adjuster: vehicle.adjuster,
			bodyStyle: vehicle?.bodyStyle,
			category: vehicle?.categoryType?.name ?? '',
			claimNumber: vehicle?.claimNumber,
			condition: vehicle.wholeUnit.condition,
			description: vehicle?.wholeUnit.description,
			dismantled: vehicle?.dismantled,
			dismantler: vehicle.dismantler?.id,
			externalColor: vehicle?.externalColor,
			externalColorCode: vehicle?.externalColorCode,
			fileNumber: vehicle.fileNumber,
			glCategoryId: vehicle.glCategory?.id,
			internalColor: vehicle?.internalColor,
			internalColorCode: vehicle?.internalColorCode,
			inventoryGlCategoryId: vehicle.inventoryGlCategory?.id,
			jobberPrice: vehicle?.wholeUnit?.jobberPrice,
			junkingNumber: vehicle?.junkingNumber,
			listPrice: vehicle?.wholeUnit?.listPrice,
			location: vehicle?.location,
			make: vehicle?.manufacturer?.name ?? '',
			mileage: vehicle?.mileage, //Odometer
			model: vehicle?.model?.name ?? '',
			notes: vehicle?.notes, //Internal Notes
			pickUpLocation: vehicle?.pickUpLocation,
			public: vehicle.public, //Show Online
			purchaseDate: vehicle?.purchaseDate,
			purchasedFrom: vehicle?.purchasedFrom,
			receivedDate: vehicle?.receivedDate,
			retailPrice: vehicle?.wholeUnit?.retailPrice,
			saleClass: {
				code: vehicle?.wholeUnit?.saleClass?.code,
			},
			sellerContact: vehicle?.sellerContact,
			sellerInfo: vehicle?.sellerInfo,
			sellerPhone: vehicle?.sellerPhone,
			status: vehicle?.status,
			stockNumber: vehicle?.stockNumber,
			taxable: vehicle.taxable,
			titleNumber: vehicle?.titleNumber,
			titleReceivedDate: vehicle?.titleReceivedDate,
			titleState: vehicle?.titleState,
			titleStatus: vehicle?.titleStatus,
			titleType: vehicle?.titleType,
			typeData1: vehicle?.wholeUnit?.typeField1?.data,
			typeData2: vehicle?.wholeUnit?.typeField2?.data,
			typeData3: vehicle?.wholeUnit?.typeField3?.data,
			typeData4: vehicle?.wholeUnit?.typeField4?.data,
			userStatus: vehicle?.userStatus,
			vin: vehicle?.vin,
			wholesalePrice: vehicle?.wholeUnit?.wholesalePrice,
			year: vehicle?.year,
			optionValues: Array.from(optionValueMap.entries()).reduce((acc: InventoryOptionValueUpdate[], [key, value]) => {
				if (key.optionId) {
					acc.push({
						inventoryOptionId: key.optionId,
						value: transformInventoryOptionValueForMutation(value),
					})
				}
				return acc
			}, []),
		}

		try {
			if (vehicle.id) {
				const res = await updateVehicleMutation.mutate({
					input: {
						...input,
						id: vehicle.id,
					},
				})
				originalVehicle = vehicle
				qaChanged = false
				if (res.data?.updateVehicle?.id) {
					asr.go(null, {
						vehicleId: res.data.updateVehicle.id,
						storeId: res.data.updateVehicle.storeId,
						lastResetTime: Date.now(),
					})
				}
			} else {
				const res = await createVehicleMutation.mutate({
					input: {
						...input,
						inventoryTypeId: vehicle.wholeUnit.inventoryTypeId,
						storeId: vehicle.store.id,
					},
				})
				originalVehicle = vehicle
				qaChanged = false
				if (res.data?.createVehicle?.id) {
					asr.go(null, {
						vehicleId: res.data.createVehicle.id,
						storeId: res.data.createVehicle.storeId,
						lastResetTime: Date.now(),
					})
				}
			}

			mediator.publish('showMessage', {
				type: 'success',
				heading: translate('common:savedHeading', 'Saved!'),
				message: translate('vehicle.vehicleSavedSuccessfully', 'Vehicle saved successfully.'),
				time: 10,
			})
		} catch (err) {
			mediator.publish('showMessage', {
				type: 'danger',
				heading: translate('common:errorHeading', 'Error!'),
				message: Array.isArray(err) ? err[0].message : translate('workOrder.unknownError', 'An unknown error occurred'),
				time: 10,
			})
		}
	}

	const saveVehicleUnsubscribe = mediator.provide('saveVehicle', () => saveVehicle(vehicle, optionValueMap))

	const splitString = (string: string | null): [string, string] => {
		if (string === null) return ['', '']
		const array = string?.split(' ')?.map(part => part?.replace(/[^a-zA-Z0-9]/g, ''))
		return [array?.[0] ?? '', array?.[1] ?? '']
	}

	function getRequiredFieldsComplete(
		vehicle: Vehicle | undefined,
		originalVehicle: Vehicle | undefined,
		settingValues?: SettingValues,
	) {
		const requiredFieldKeys = ['year', 'manufacturer', 'model', 'categoryType']
		for (const key of requiredFieldKeys) {
			if ((!vehicle?.id || !dequal(vehicle[key], originalVehicle?.[key])) && !vehicle?.[key]) {
				return false
			}
		}

		if (vehicle?.year && !isValidYear(vehicle.year)) {
			return false
		}

		if (
			settingValues?.vehicle?.mileageRequired &&
			(!vehicle?.id || vehicle?.mileage !== originalVehicle?.mileage) &&
			!vehicle?.mileage
		) {
			return false
		}
		if (
			settingValues?.vehicle?.minVinLength &&
			(!vehicle?.id || vehicle?.vin !== originalVehicle?.vin) &&
			vehicle?.vin &&
			vehicle?.vin.length < settingValues.vehicle?.minVinLength
		) {
			return false
		}

		return true
	}

	async function loadAndSetModels(wholeUnitTypeId: number, manufacturerId?: number) {
		try {
			if (manufacturerId) {
				isLoadingModels = true
				vehicleModels = await loadPartModelsForInventoryTypeAndManufacturer({
					inventoryTypeId: wholeUnitTypeId,
					manufacturerId,
				})
			} else {
				vehicleModels = []
			}
		} finally {
			isLoadingModels = false
		}
	}

	const getSellerContactFromIndividual = (lastName: string, firstName: string) =>
		[lastName, firstName].filter(v => v).join(', ')
	const getIndividualFromSellerContact = (sellerContact: string) => {
		const [lastName, firstName] = splitString(sellerContact)
		return { lastName, firstName }
	}

	function setSellerContact(isCompany: boolean) {
		if (isCompany) {
			sellerContact = getSellerContactFromIndividual(sellerContactLastName, sellerContactFirstName)
			vehicle.sellerContact = sellerContact
		} else {
			const { firstName, lastName } = getIndividualFromSellerContact(sellerContact)
			sellerContactLastName = lastName
			sellerContactFirstName = firstName
			vehicle.sellerContact = sellerContact
		}
	}

	async function loadInventoryTypeDetailManufacturersCategories(inventoryTypeId: number | null) {
		if (inventoryTypeId) {
			isLoadingInventoryTypeDetailManufacturersCategories = true
			try {
				const res = await unitTypeDetailQuery.fetch({ variables: { inventoryTypeId } })

				if (res.data?.inventoryType) {
					inventoryTypeWithDetail = res.data.inventoryType

					// We need to update the typeField labels to match the new inventory type. This is being done one the vehicle object, as multiple
					// places in the code read this property to know what to display.
					if (vehicle?.wholeUnit?.typeField1) {
						vehicle.wholeUnit.typeField1.label = res.data.inventoryType.typeLabel1
					}
					if (vehicle?.wholeUnit?.typeField2) {
						vehicle.wholeUnit.typeField2.label = res.data.inventoryType.typeLabel2
					}
					if (vehicle?.wholeUnit?.typeField3) {
						vehicle.wholeUnit.typeField3.label = res.data.inventoryType.typeLabel3
					}
					if (vehicle?.wholeUnit?.typeField4) {
						vehicle.wholeUnit.typeField4.label = res.data.inventoryType.typeLabel4
					}

					vehicleManufacturers = res.data.inventoryType.partManufacturers ?? []
					categories = res.data.inventoryType.categories ?? []
				}
			} finally {
				isLoadingInventoryTypeDetailManufacturersCategories = false
			}
		}
	}

	export function canLeaveState() {
		if (vehicleChanged) {
			return confirm(
				translate(
					'common:youHaveUnsavedChangesAreYouSureYouWantToLeave',
					'You have unsaved changes. Are you sure you want to leave?',
				),
			)
		}
		return true
	}

	onMount(async () => {
		window.scrollTo(0, 0)
		if (!vehicle.id) {
			await tick()
			document.getElementById('vinInput')?.focus()
		}

		if (options.length) {
			optionValueMap = getOptionValueMap({ inventoryOptions: options, serialized: false, serials: [] })
		}
		loadInventoryTypeDetailManufacturersCategories(vehicle.wholeUnit.inventoryTypeId).catch(err => console.error(err))
	})

	onDestroy(() => {
		saveVehicleUnsubscribe?.()
	})
</script>

<fieldset disabled={!canEditBasic}>
	<Card
		collapsible
		header={translate('common:basicInfo', 'Basic Info')}
		headerClass="card-header d-flex justify-content-between h4"
		entireHeaderToggles
		bind:bodyShown={$cardsOpen.basic}
	>
		<div class="row">
			{#if vehicle.topImage}
				<div class="col-sm-12 col-md-2">
					<ImagePreview
						altTitle={`Stock #${vehicle.stockNumber}`}
						selectedImage={vehicle.topImage}
						images={vehicle.attachments
							.filter(attachment => attachment.file.mimeType.startsWith('image/'))
							.map(attachment => attachment.file)}
					/>
				</div>
			{/if}
			<div
				class="col-12"
				class:col-md-8={vehicle.topImage}
				class:col-md-10={!vehicle.topImage}
				class:col-lg-7={vehicle.topImage}
				class:col-lg-9={!vehicle.topImage}
			>
				<div class="form-row">
					<div class="col-sm-12 col-md-4">
						<Input
							label={translate('common:stockNum', 'Stock #')}
							placeholder={translate('vehicle.leaveBlankToAutoGenerateOnSave', 'Leave blank to auto-generate on save')}
							title={translate(
								'vehicle.stockNumberTitleHint',
								'(Optional) enter a unique stock # for this vehicle unit. If left blank, one will automatically be assigned.',
							)}
							hint={getModifiedHint('stockNumber')}
							disabled={!canEditStockNumber}
							bind:value={vehicle.stockNumber}
						/>
					</div>
					<div class="col-sm-12 col-md-4">
						<Input
							label={translate('common:vin', 'VIN')}
							id="vinInput"
							class="form-control form-control-sm"
							placeholder={translate('part.vehicleIdentificationNumber', 'Vehicle Identification Number')}
							title={translate(
								'vehicle.VINTitleHint',
								'The VIN from the unit. If installed, the VIN decoder system will attempt to decode this value.',
							)}
							required={!!settingValues.vehicle.minVinLength && settingValues.vehicle.minVinLength > 0}
							disabled={vinLoading}
							validation={{
								validator: vin => {
									return !!(settingValues.vehicle.minVinLength,
									vin && typeof vin === 'string' && vin.length >= settingValues.vehicle.minVinLength)
								},
								value: vehicle?.vin,
							}}
							onchange={async event => {
								const vin = getEventValue(event)
								vinLoading = true
								const duplicates = await checkForDuplicateVin(vin)
								if (duplicates.length === 0) {
									await decodeVin(vin)
								}
								vinLoading = false
							}}
							bind:value={vehicle.vin}
						>
							{#snippet append()}
								<Button
									outline
									size="sm"
									iconClass="magnifying-glass"
									isLoading={vinLoading}
									title={translate('vehicle.VINButtonTitleHint', 'View VIN decode information and/or enter a new VIN.')}
									onclick={() => vinLookupModal.open(vehicle.vin ?? '')}
								/>
							{/snippet}
							{#snippet hint()}
								{@render getModifiedHint('vin')?.()}
								<small class:text-danger={vehicle.vin && vinValidationError}
									>{vehicle.vin ? vinValidationError : ''}</small
								>
							{/snippet}
						</Input>
					</div>
					<div class="col-sm-12 col-md-4">
						<Select
							required
							label={translate('common:type', 'Type')}
							title={translate(
								'vehicle.typeInputTitleHint',
								'(Required) choose the unit type. This controls the Q&A available, the categories, and some other information.',
							)}
							showEmptyOption={false}
							disabled={!!vehicle?.id}
							hint={!dequal(vehicle.wholeUnit.inventoryTypeId, originalVehicle.wholeUnit.inventoryTypeId)
								? modifiedHint
								: undefined}
							onchange={event => {
								const typeId = parseInt(getEventValue(event), 10)
								if (typeId) {
									loadInventoryTypeDetailManufacturersCategories(typeId)
								}
							}}
							bind:value={vehicle.wholeUnit.inventoryTypeId}
						>
							{#each unitTypes as { id, name }}
								<option value={id}>{name}</option>
							{/each}
						</Select>
					</div>

					<div class="col-sm-12 col-md-4">
						<YearInput
							required
							label={translate('common:year', 'Year')}
							title={translate('vehicle.yearOfManufacture', 'Year of manufacture')}
							validation={{
								validator: isValidYear,
								value: vehicle?.year,
							}}
							value={vehicle.year}
							isLoading={vinLoading}
							hint={getModifiedHint('year')}
							change={year => (vehicle.year = year)}
						/>
					</div>
					<div class="col-sm-12 col-md-4">
						<Autocomplete
							required
							label={translate('common:make', 'Make')}
							hint={getModifiedHint('manufacturer')}
							options={vehicleManufacturers}
							getLabel={option => option?.name ?? ''}
							disabled={vinLoading}
							isLoading={isLoadingInventoryTypeDetailManufacturersCategories}
							bind:value={vehicle.manufacturer}
							change={async selectedManufacturer => {
								let previousVehicleModel = klona(vehicle.model)
								if (dequal(selectedManufacturer, originalVehicle.manufacturer)) {
									vehicle.model = klona(originalVehicle.model)
								}
								if (vehicle.wholeUnit.inventoryTypeId && vehicle.manufacturer?.id) {
									await loadAndSetModels(vehicle.wholeUnit.inventoryTypeId, vehicle.manufacturer?.id)
								} else {
									vehicleModels = []
								}
								if (previousVehicleModel && !vehicleModels.find(model => model.id === previousVehicleModel.id)) {
									vehicle.model = null
								}
							}}
						/>
					</div>

					<div class="col-sm-12 col-md-4">
						<Autocomplete
							required
							label={translate('common:model', 'Model')}
							title={translate('vehicle.theModelOfThisUnit', 'The model of this unit')}
							disabled={vinLoading}
							options={vehicleModels}
							isLoading={isLoadingModels}
							hint={getModifiedHint('model')}
							getLabel={option => option?.name ?? ''}
							bind:value={vehicle.model}
						/>
					</div>
					<div class="col-sm-12 col-md-4">
						<Select
							label={translate('common:status', 'Status')}
							title={translate(
								'vehicle.statusTitleHint',
								'Current vehicle status. Key:&#013;B (Bid): a bid has been submitted, but not awarded.&#013;A (Available): the unit has been received.&#013;H (On Hold): the unit is received but currently on hold.&#013;S (Sold): the unit has been sold.&#013;C (Crushed): the unit has been crushed/scrapped.&#013;D (Deleted): this unit was a duplicate or mistake. Do not use this for vehicles that have been dismantled, use Crushed instead.',
							)}
							hint={getModifiedHint('status')}
							disabled={!canEditStatus}
							bind:value={vehicle.status}
						>
							{#each vehicleStatuses as { abbreviation, name }}
								<option
									value={abbreviation}
									disabled={abbreviation === 'B' && vehicle.status !== 'B'}>{abbreviation} - {name}</option
								>
							{/each}
						</Select>
					</div>
					<div class="col-sm-12 col-md-4">
						<Select
							label={translate('common:userStatus', 'User Status')}
							title={translate('vehicle.userStatusTitleHint', 'Custom company status (if any).')}
							hint={getModifiedHint('userStatus')}
							disabled={!canEditStatus}
							bind:value={vehicle.userStatus}
						>
							{#each userStatuses as { status, description }}
								<option value={status}>{status} - {description}</option>
							{/each}
						</Select>
					</div>
					<div class="col-sm-12 col-md-4">
						<Autocomplete
							label={translate('common:location', 'Location')}
							title={translate('vehicle.locationOfTheUnitInTheYard', 'Location of the unit in the yard')}
							options={locations}
							hint={getModifiedHint('location')}
							bind:value={vehicle.location}
						/>
					</div>
					<div class="col-sm-12 col-md-4">
						<datalist id="vehicle-color-codes">
							{#each colors.vehicleColorCodes as name}
								<option value={name}></option>
							{/each}
						</datalist>
						<datalist id="vehicle-colors">
							{#each colors.vehicleColors as name}
								<option value={name}></option>
							{/each}
						</datalist>
						<label
							for="exterior-vehicle-color"
							class="col-form-label">{translate('vehicle.exteriorColorCode', 'Exterior Color/Code')}</label
						>
						{@render (getModifiedHint('externalColor') || getModifiedHint('externalColorCode'))?.()}
						<div class="input-group input-group-sm">
							<input
								id="exterior-vehicle-color"
								list="vehicle-colors"
								placeholder={translate('common:color', 'Color')}
								aria-label="exterior color"
								class="form-control"
								title={translate('vehicle.theExteriorColor', 'The exterior color')}
								bind:value={vehicle.externalColor}
							/>
							<input
								list="vehicle-color-codes"
								placeholder={translate('vehicle.colorCode', 'Color Code')}
								aria-label="exterior color code"
								class="form-control"
								title={translate('vehicle.colorCodeTitleHint', 'A paint code for the exterior color of the unit.')}
								bind:value={vehicle.externalColorCode}
							/>
						</div>
					</div>
					<div class="col-sm-12 col-md-4">
						<label
							for="internal-vehicle-color"
							class="col-form-label">{translate('vehicle.internalColorCode', 'Internal Color/Code')}</label
						>
						{@render (getModifiedHint('internalColor') || getModifiedHint('internalColorCode'))?.()}
						<div class="input-group input-group-sm">
							<input
								id="internal-vehicle-color"
								list="vehicle-colors"
								placeholder={translate('common:color', 'Color')}
								aria-label="internal color"
								class="form-control"
								title={translate('vehicle.theInteriorColor', 'The interior color')}
								bind:value={vehicle.internalColor}
							/>
							<input
								list="vehicle-color-codes"
								placeholder={translate('vehicle.colorCode', 'Color Code')}
								aria-label="internal color code"
								class="form-control"
								title={translate(
									'vehicle.internalColorCodeTitleHint',
									'A paint code for the interior color of the unit.',
								)}
								bind:value={vehicle.internalColorCode}
							/>
						</div>
					</div>
					<div class="col-sm-12 col-md-4">
						<Input
							label={translate('common:bodyStyle', 'Body Style')}
							title={translate('vehicle.generalBodyStyleOfTheUnit', 'General body style of the unit.')}
							hint={getModifiedHint('bodyStyle')}
							bind:value={vehicle.bodyStyle}
						/>
					</div>
					<div class="col-sm-12 col-md-4">
						<Input
							type="number"
							label={translate('vehicle.odometer', 'Odometer')}
							title={translate('vehicle.currentOdometerReading', 'Current odometer reading.')}
							hint={getModifiedHint('mileage')}
							required={settingValues.vehicle.mileageRequired}
							bind:value={vehicle.mileage}
						/>
					</div>
					<div class="col-sm-12 col-md-4">
						<div class="label-padding">
							<Checkbox
								label={translate('common:dismantled', 'Dismantled')}
								hint={getModifiedHint('dismantled') || getModifiedHint('dismantler')}
								bind:checked={vehicle.dismantled}
							/>
						</div>
						<Autocomplete
							label={translate('common:dismantler', 'Dismantler')}
							showLabel={false}
							emptyValue={null}
							options={userAccounts}
							getLabel={option => option?.name ?? ''}
							title={translate(
								'vehicle.theTechnicianThatDismantledTheUnit',
								'The technician that dismantled the unit.',
							)}
							bind:value={vehicle.dismantler}
						/>
					</div>
					<div class="col-sm-12 col-md-4">
						<DateInput
							allowNone
							label={translate('vehicle.dismantleDate', 'Dismantle Date')}
							title={translate(
								'vehicle.dismantleDateTitleHint',
								'For a unit that is being dismantled into components, when the unit completed the dismantle process.',
							)}
							value={vehicle.dismantleDate ? formatISO(vehicle.dismantleDate, { representation: 'date' }) : null}
							hint={getModifiedHint('dismantleDate')}
							onchange={event => {
								const value = getEventValue(event)
								vehicle.dismantleDate = value ? parseISO(value) : null
							}}
							allowDateChange={allowDateSelection => {
								if (!allowDateSelection) {
									vehicle.dismantleDate = null
								}
							}}
						/>
					</div>

					<div class="col-sm-12 col-md-4">
						<SiteAutocomplete
							label={translate('common:store', 'Store')}
							disabled={!!vehicle.id}
							showEmptyOption={false}
							options={$session.authorizedStores}
							hint={getModifiedHint('store')}
							bind:value={vehicle.store}
						/>
					</div>
					<div class="col-sm-12 col-md-4">
						<Autocomplete
							label={translate('vehicle.vehicleGlCategory', 'Vehicle GL Category')}
							title={translate(
								'vehicle.vehicleGlCategoryTitleHint',
								'The accounting category for this unit. This will control what accounts are affected if this vehicle is sold outright.',
							)}
							emptyValue={null}
							options={glCategories}
							disabled={!canEditGlCategory}
							getLabel={option => option?.name ?? ''}
							hint={getModifiedHint('glCategory')}
							bind:value={vehicle.glCategory}
						/>
					</div>
					<div class="col-sm-12 col-md-4">
						<Autocomplete
							label={translate('vehicle.partsGlCategory', 'Parts GL Category')}
							title={translate(
								'vehicle.partsGlCategoryTitleHint',
								'The accounting category for inventory dismantled from this unit. This will control what accounts are affected if components from this vehicle are consumed or sold.',
							)}
							emptyValue={null}
							options={glCategories}
							disabled={!canEditGlCategory}
							getLabel={option => option?.name ?? ''}
							hint={getModifiedHint('inventoryGlCategory')}
							bind:value={vehicle.inventoryGlCategory}
						/>
					</div>
				</div>
			</div>
			<div class="col-12 col-md-2 col-lg-3 pl-md-0">
				<TextArea
					class="flex-grow-1"
					label={translate('common:internalNotes', 'Internal Notes')}
					title={translate('vehicle.internalNotesAboutThisUnit', 'Internal notes about this unit.')}
					labelParentClass="h-100 d-flex flex-column"
					placeholder={translate(
						'part.internalNotesplaceholder',
						"Internal notes won't appear on any customer-facing reports or online.",
					)}
					disabled={vinLoading}
					hint={getModifiedHint('notes')}
					bind:value={vehicle.notes}
				/>
			</div>
		</div>

		{#if vehicle.wholeUnit.inventoryTypeId && inventoryTypeWithDetail}
			<div class="card mt-3">
				<h5 class="card-header">{translate('common:customFields', 'Custom Fields')}</h5>
				<div class="card-body">
					<QaList
						{options}
						{optionValueMap}
						serials={[]}
						shouldDisable={() => !canEditQanda}
						category={vehicle.categoryType ?? null}
						inventoryTypeData={inventoryTypeWithDetail}
						typeFields={computeTypeFields(vehicle.wholeUnit, settingValues)}
						typeFieldChange={({ label, value }) => {
							if (vehicle.wholeUnit.typeField1?.label === label) {
								vehicle.wholeUnit.typeField1.data = value
							} else if (vehicle.wholeUnit.typeField2?.label === label) {
								vehicle.wholeUnit.typeField2.data = value
							} else if (vehicle.wholeUnit.typeField3?.label === label) {
								vehicle.wholeUnit.typeField3.data = value
							} else if (vehicle.wholeUnit.typeField4?.label === label) {
								vehicle.wholeUnit.typeField4.data = value
							}
						}}
						qaInputChange={updatedOption => {
							if (updatedOption.value === null || updatedOption.value === '') {
								optionValueMap.delete({ optionId: updatedOption.id })
							} else {
								optionValueMap.set({ optionId: updatedOption.id }, updatedOption.value)
							}
							//Because the option values are stored in a separate map, we need to manually emit that the vehicle has changed
							qaChanged = true
						}}
						bind:this={qaList}
					/>
				</div>
			</div>
		{/if}
	</Card>
</fieldset>

<!-- The marketing component handles permissions for these marketing fields -->
<fieldset>
	<Card
		collapsible
		headerClass="card-header d-flex justify-content-between h4"
		entireHeaderToggles
		class="mt-2"
		bind:bodyShown={$cardsOpen.salesMarketing}
	>
		{#snippet header()}
			<span
				>{translate('vehicle.salesMarketing', 'Sales & Marketing')}
				<HtpInfoButton
					sku={vehicle.wholeUnit.inventoryId}
					storeId={vehicle.wholeUnit.storeId}
				></HtpInfoButton>
			</span>
		{/snippet}
		<Checkbox
			inline
			label={translate('common:taxable', 'Taxable')}
			title={translate(
				'vehicle.taxableTitleHint',
				'Whether this unit will be marked as taxable on customer documents by default',
			)}
			disabled={!canEditBasic}
			hint={getModifiedHint('taxable')}
			bind:checked={vehicle.taxable}
		/>
		<Checkbox
			inline
			label={translate('common:showOnline', 'Show Online')}
			title={translate(
				'vehicle.showOnlineTitleHint',
				'If checked, any configured marketing exports are allowed to view this unit and advertise it',
			)}
			disabled={!canEditBasic}
			hint={getModifiedHint('public')}
			bind:checked={vehicle.public}
		/>
		<!-- We don't currently support adjusting the in stock flag on web. Apparently it will be a bit of a lift on the API side, at least. -->
		<Checkbox
			inline
			disabled
			label={translate('vehicle.unitAvailableInStock)', 'Unit Available (In Stock)')}
			checked={vehicle.inStock}
		/>
		<!-- `originalVehicle` is a deep copy of `vehicle` -->
		<PartMarketingFields
			{field}
			{vehicle}
			{saleClasses}
			{inventoryConditions}
			entityContext="vehicle"
			origPart={originalVehicle.wholeUnit}
			inventoryTypeCategories={categories}
			modelName={vehicle?.model?.name ?? ''}
			shouldDisable={() => !canEditWholeUnits}
			manufacturerName={vehicle?.manufacturer?.name ?? ''}
			categoryChange={async () => {
				if (qaList) {
					await qaList.fetchNewQuestions({
						categoryName: vehicle.wholeUnit.category?.name ?? '',
						manufacturerId: vehicle.manufacturer?.id ?? null,
						modelId: vehicle.model?.id ?? null,
						inventoryTypeId: vehicle.wholeUnit.inventoryTypeId,
					})
				}
			}}
			part={vehicle.wholeUnit}
		/>
	</Card>
</fieldset>

<fieldset disabled={!canEditAdditionalInfo}>
	<Card
		collapsible
		header={translate('vehicle.sellerPurchasing', 'Seller & Purchasing')}
		headerClass="card-header d-flex justify-content-between h4"
		entireHeaderToggles
		class="mt-2"
		bind:bodyShown={$cardsOpen.purchaseSeller}
	>
		<div class="form-row align-items-end">
			<div class="col-sm-12 col-md-4">
				<Checkbox
					type="radio"
					trueLabel={translate('common:company', 'Company')}
					falseLabel={translate('common:individual', 'Individual')}
					showLabel={false}
					onchange={event => {
						const isCompany = stringToBoolean(getEventValue(event))
						vehicle.sellerIsCompany = isCompany
						setSellerContact(isCompany)
					}}
					bind:checked={vehicle.sellerIsCompany}
				/>
				{@render getModifiedHint('sellerIsCompany')?.()}
				{#if vehicle.sellerIsCompany}
					<Input
						label={translate('vehicle.sellerContact', 'Seller Contact')}
						value={sellerContact}
						onchange={event => {
							sellerContact = getEventValue(event)
							const { lastName, firstName } = getIndividualFromSellerContact(sellerContact)
							sellerContactLastName = lastName
							sellerContactFirstName = firstName
							vehicle.sellerContact = sellerContact
						}}
						hint={getModifiedHint('sellerContact')}
					/>
				{:else}
					<div class="form-group mb-1">
						<label
							for="sellerContactFirstName"
							class="col-form-label">{translate('vehicle.sellerContact', 'Seller Contact')}</label
						>
						{@render getModifiedHint('sellerContact')?.()}
						<div class="input-group">
							<input
								id="sellerContactLastName"
								aria-label="Last name"
								placeholder={translate('common:lastName', 'Last Name')}
								class="form-control form-control-sm"
								value={sellerContactLastName}
								onchange={event => {
									sellerContactLastName = getEventValue(event)
									sellerContact = getSellerContactFromIndividual(sellerContactLastName, sellerContactFirstName)
									vehicle.sellerContact = sellerContact
								}}
							/>
							<input
								id="sellerContactFirstName"
								aria-label="First name"
								placeholder={translate('common:firstName', 'First Name')}
								class="form-control form-control-sm"
								value={sellerContactFirstName}
								onchange={event => {
									sellerContactFirstName = getEventValue(event)
									sellerContact = getSellerContactFromIndividual(sellerContactLastName, sellerContactFirstName)
									vehicle.sellerContact = sellerContact
								}}
							/>
						</div>
					</div>
				{/if}
			</div>
			<div class="col-sm-12 col-md-4">
				<Input
					label={translate('common:sellerPhoneNum', 'Seller Phone #')}
					hint={getModifiedHint('sellerPhone')}
					bind:value={vehicle.sellerPhone}
				/>
			</div>
			<div class="col-sm-12 col-md-6">
				<TextArea
					label={translate('common:sellerInfo', 'Seller Info')}
					hint={getModifiedHint('sellerInfo')}
					bind:value={vehicle.sellerInfo}
				/>
			</div>
			<div class="col-sm-12 col-md-6">
				<TextArea
					label={translate('vehicle.pickUpLocation', 'Pick-up Location')}
					hint={getModifiedHint('pickUpLocation')}
					bind:value={vehicle.pickUpLocation}
				/>
			</div>
			<div class="col-sm-12 col-md-4">
				<DateInput
					allowNone
					label={translate('common:purchaseDate', 'Purchase Date')}
					title={translate(
						'vehicle.purchaseDateTitleHint',
						'The date the unit was purchased. This will be set automatically when a vehicle bid is accepted.',
					)}
					value={vehicle.purchaseDate ? formatISO(vehicle.purchaseDate, { representation: 'date' }) : null}
					hint={getModifiedHint('purchaseDate')}
					onchange={event => {
						const value = getEventValue(event)
						vehicle.purchaseDate = value ? parseISO(value) : null
					}}
					allowDateChange={allowDateSelection => {
						if (!allowDateSelection) {
							vehicle.purchaseDate = null
						}
					}}
				/>
			</div>
			<div class="col-sm-12 col-md-4">
				<Input
					label={translate('common:purchasedFrom', 'Purchased From')}
					hint={getModifiedHint('purchasedFrom')}
					title={translate(
						'vehicle.purchasedFromTitleHint',
						'Who the unit was purchased from. This will be set automatically when a vehicle bid is accepted.',
					)}
					bind:value={vehicle.purchasedFrom}
				/>
			</div>
			<div class="col-sm-12 col-md-4">
				<DateInput
					allowNone
					label={translate('vehicle.unitReceivedDate', 'Unit Received Date')}
					title={translate(
						'vehicle.unitReceivedDateTitleHint',
						'When the unit was received from the seller and went through the inbounding process.',
					)}
					value={vehicle.receivedDate ? formatISO(vehicle.receivedDate, { representation: 'date' }) : null}
					hint={getModifiedHint('receivedDate')}
					onchange={event => {
						const value = getEventValue(event)
						vehicle.receivedDate = value ? parseISO(value) : null
					}}
					allowDateChange={allowDateSelection => {
						if (!allowDateSelection) {
							vehicle.receivedDate = null
						}
					}}
				/>
			</div>
		</div>
	</Card>

	<Card
		collapsible
		header={translate('common:title', 'Title')}
		headerClass="card-header d-flex justify-content-between h4"
		entireHeaderToggles
		class="mt-2"
		bind:bodyShown={$cardsOpen.title}
	>
		<div class="form-row">
			<div class="col-sm-12 col-md-4">
				<DateInput
					allowNone
					label={translate('common:titleReceived', 'Title Received')}
					hint={getModifiedHint('titleReceivedDate')}
					value={vehicle.titleReceivedDate ? formatISO(vehicle.titleReceivedDate, { representation: 'date' }) : null}
					onchange={event => {
						const value = getEventValue(event)
						vehicle.titleReceivedDate = value ? parseISO(value) : null
					}}
					allowDateChange={allowDateSelection => {
						if (!allowDateSelection) {
							vehicle.titleReceivedDate = null
						}
					}}
				/>
			</div>
			<div class="col-sm-12 col-md-4">
				<Input
					label={translate('common:titleNum', 'Title #')}
					hint={getModifiedHint('titleNumber')}
					bind:value={vehicle.titleNumber}
				/>
			</div>
			<div class="col-sm-12 col-md-4">
				<!-- <Select
					label="Title State"
					bind:value={vehicle.titleState}
				>
					{#each Object.keys(statesByCountry) as country}
						<optgroup label={country}>
							{#each statesByCountry[country] as state}
								<option value={state.stateAbbreviation}>{state.stateAbbreviation} - {state.stateName}</option>
							{/each}
						</optgroup>
					{/each}
				</Select> -->
			</div>
			<div class="col-sm-12 col-md-4">
				<Input
					label={translate('common:titleStatus', 'Title Status')}
					hint={getModifiedHint('titleStatus')}
					bind:value={vehicle.titleStatus}
				/>
			</div>
			<div class="col-sm-12 col-md-4">
				<Input
					label={translate('common:titleType', 'Title Type')}
					hint={getModifiedHint('titleType')}
					bind:value={vehicle.titleType}
				/>
			</div>
		</div>
	</Card>

	<Card
		collapsible
		header={translate('vehicle.insurance', 'Insurance')}
		headerClass="card-header d-flex justify-content-between h4"
		entireHeaderToggles
		class="mt-2"
		bind:bodyShown={$cardsOpen.insurance}
	>
		<div class="form-row">
			<div class="col-sm-12 col-md-4">
				<Input
					label={translate('common:adjuster', 'Adjuster')}
					hint={getModifiedHint('adjuster')}
					bind:value={vehicle.adjuster}
				/>
			</div>
			<div class="col-sm-12 col-md-4">
				<Input
					label={translate('common:claimNum', 'Claim #')}
					hint={getModifiedHint('claimNumber')}
					bind:value={vehicle.claimNumber}
				/>
			</div>
			<div class="col-sm-12 col-md-4">
				<Input
					label={translate('common:fileNum', 'File #')}
					hint={getModifiedHint('fileNumber')}
					bind:value={vehicle.fileNumber}
				/>
			</div>
			<div class="col-sm-12 col-md-4">
				<Input
					label={translate('common:junkingNum', 'Junking #')}
					hint={getModifiedHint('junkingNumber')}
					bind:value={vehicle.junkingNumber}
				/>
			</div>
		</div></Card
	>
</fieldset>

<!-- In the future we might want to show the confirm button and do something different when it's pressed, like take the the VIN entered on the modal and put it on the vehicle record? -->
<VinLookupModal
	bind:this={vinLookupModal}
	confirm={async ({ vin }) => {
		vinLoading = true
		const duplicates = await checkForDuplicateVin(vin)
		if (duplicates.length === 0) {
			vehicle.vin = vin
			await decodeVin(vin)
		}
		vinLoading = false
	}}
/>
<DuplicateVinModal
	bind:this={duplicateVinModal}
	bind:show={showDuplicateVinModal}
	{asr}
	close={() => {
		vehicle.vin = ''
		showDuplicateVinModal = false
		document.getElementById('vinInput')?.focus()
	}}
	continueWithDuplicateVin={vin => {
		vehicle.vin = vin
		decodeVin(vin)
		showDuplicateVinModal = false
	}}
/>

{#snippet modifiedHint(iconClass?: string)}
	<Icon
		class="text-warning {iconClass}"
		icon="pencil"
		size="xs"
	/>
{/snippet}

<style>
	.label-padding {
		padding-top: calc(0.375rem + 1px);
		padding-bottom: calc(0.375rem + 1px);
	}
</style>
